import { listAll, putRecord } from "./db";
import type { BackupManifest, MediaRecord } from "../types/app";
import { createId } from "../utils/id";
import { blobToArrayBuffer } from "../utils/blob";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let index = 0; index < 8; index += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

function writeUint32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

function dosDateTime(date = new Date()): { date: number; time: number } {
  const year = Math.max(date.getFullYear(), 1980);
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

interface ZipEntry {
  path: string;
  data: Uint8Array;
}

export async function createBackupZip(): Promise<Blob> {
  const media = (await listAll("media")) as MediaRecord[];
  const manifest: BackupManifest = {
    version: 1,
    exportedAt: new Date().toISOString(),
    settings: await listAll("settings"),
    memories: await listAll("memories"),
    conversations: await listAll("conversations"),
    messages: await listAll("messages"),
    voiceProfiles: await listAll("voiceProfiles"),
    media: media.map((record) => ({
      id: record.id,
      type: record.type,
      filename: record.filename,
      mimeType: record.mimeType,
      state: record.state,
      metadata: record.metadata,
      createdAt: record.createdAt,
      path: `media/${record.id}-${record.filename.replace(/[^\w.-]/g, "_")}`,
    })),
  };
  const entries: ZipEntry[] = [
    { path: "manifest.json", data: encoder.encode(JSON.stringify(manifest, null, 2)) },
  ];
  for (const record of media) {
    const meta = manifest.media.find((item) => item.id === record.id);
    if (meta) entries.push({ path: meta.path, data: new Uint8Array(await blobToArrayBuffer(record.blob)) });
  }
  return buildZip(entries);
}

export async function importBackupZip(file: File): Promise<void> {
  const entries = await readZip(file);
  const manifestBytes = entries.get("manifest.json");
  if (!manifestBytes) throw new Error("バックアップのmanifest.jsonが見つかりません。");
  const manifest = JSON.parse(decoder.decode(manifestBytes)) as BackupManifest;
  const blobByPath = new Map<string, Blob>();
  for (const item of manifest.media) {
    const bytes = entries.get(item.path);
    if (bytes) blobByPath.set(item.path, new Blob([arrayBufferPart(bytes)], { type: item.mimeType }));
  }
  for (const setting of manifest.settings) await putRecord("settings", setting);
  for (const memory of manifest.memories) await putRecord("memories", memory);
  for (const conversation of manifest.conversations) await putRecord("conversations", conversation);
  for (const message of manifest.messages) await putRecord("messages", message);
  for (const profile of manifest.voiceProfiles) await putRecord("voiceProfiles", profile);
  for (const item of manifest.media) {
    const blob = blobByPath.get(item.path);
    if (blob) await putRecord("media", { ...item, id: item.id || createId("media"), blob });
  }
}

function buildZip(entries: ZipEntry[]): Blob {
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const { date, time } = dosDateTime();
  for (const entry of entries) {
    const name = encoder.encode(entry.path);
    const crc = crc32(entry.data);
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    writeUint32(lv, 0, 0x04034b50);
    writeUint16(lv, 4, 20);
    writeUint16(lv, 8, 0);
    writeUint16(lv, 10, time);
    writeUint16(lv, 12, date);
    writeUint32(lv, 14, crc);
    writeUint32(lv, 18, entry.data.length);
    writeUint32(lv, 22, entry.data.length);
    writeUint16(lv, 26, name.length);
    local.set(name, 30);
    chunks.push(local, entry.data);

    const header = new Uint8Array(46 + name.length);
    const hv = new DataView(header.buffer);
    writeUint32(hv, 0, 0x02014b50);
    writeUint16(hv, 4, 20);
    writeUint16(hv, 6, 20);
    writeUint16(hv, 10, 0);
    writeUint16(hv, 12, time);
    writeUint16(hv, 14, date);
    writeUint32(hv, 16, crc);
    writeUint32(hv, 20, entry.data.length);
    writeUint32(hv, 24, entry.data.length);
    writeUint16(hv, 28, name.length);
    writeUint32(hv, 42, offset);
    header.set(name, 46);
    central.push(header);
    offset += local.length + entry.data.length;
  }
  const centralSize = central.reduce((sum, item) => sum + item.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  writeUint32(ev, 0, 0x06054b50);
  writeUint16(ev, 8, entries.length);
  writeUint16(ev, 10, entries.length);
  writeUint32(ev, 12, centralSize);
  writeUint32(ev, 16, offset);
  return new Blob([...chunks, ...central, end].map(arrayBufferPart), { type: "application/zip" });
}

async function readZip(file: File): Promise<Map<string, Uint8Array>> {
  const bytes = new Uint8Array(await blobToArrayBuffer(file));
  const entries = new Map<string, Uint8Array>();
  let offset = 0;
  while (offset + 30 < bytes.length) {
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset);
    if (view.getUint32(0, true) !== 0x04034b50) break;
    const method = view.getUint16(8, true);
    if (method !== 0) throw new Error("圧縮されたZIPはこのインポートでは未対応です。");
    const compressedSize = view.getUint32(18, true);
    const nameLength = view.getUint16(26, true);
    const extraLength = view.getUint16(28, true);
    const nameStart = offset + 30;
    const dataStart = nameStart + nameLength + extraLength;
    const name = decoder.decode(bytes.slice(nameStart, nameStart + nameLength));
    entries.set(name, bytes.slice(dataStart, dataStart + compressedSize));
    offset = dataStart + compressedSize;
  }
  return entries;
}

function arrayBufferPart(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
